import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  partnerWidgetRequest,
  type PartnerWidgetAvailability,
  type PartnerWidgetCatalogItem,
  type PartnerWidgetLocale,
} from '@/lib/forfeoReservePartnerWidgetClient';

const copy = {
  fr: {
    title: 'Réserver avec Forfeo',
    service: 'Service',
    date: 'Date',
    people: 'Personnes',
    search: 'Voir les disponibilités',
    choose: 'Choisir',
    loading: 'Chargement…',
    unavailable: 'Aucune disponibilité pour cette date.',
    powered: 'Réservation sécurisée par Forfeo',
  },
  en: {
    title: 'Book with Forfeo',
    service: 'Service',
    date: 'Date',
    people: 'Guests',
    search: 'View availability',
    choose: 'Select',
    loading: 'Loading…',
    unavailable: 'No availability for this date.',
    powered: 'Secure booking by Forfeo',
  },
} as const;

export default function ReservePartnerWidgetPage() {
  const { publicKey = '' } = useParams();
  const [params] = useSearchParams();
  const locale = (params.get('locale') === 'en' ? 'en' : 'fr') as PartnerWidgetLocale;
  const text = copy[locale];

  const [catalog, setCatalog] = useState<PartnerWidgetCatalogItem[]>([]);
  const [itemId, setItemId] = useState(params.get('item') ?? '');
  const [date, setDate] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [slots, setSlots] = useState<PartnerWidgetAvailability[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const accent = useMemo(() => {
    const candidate = params.get('accent') ?? '#5b21b6';
    return /^#[0-9a-fA-F]{6}$/.test(candidate) ? candidate : '#5b21b6';
  }, [params]);

  const partnerOrigin = useMemo(() => {
    try {
      const referrerOrigin = document.referrer
        ? new URL(document.referrer).origin
        : '';
      const claimedOrigin = params.get('parentOrigin') ?? '';
      if (!referrerOrigin || !claimedOrigin) return '';
      return referrerOrigin === claimedOrigin ? referrerOrigin : '';
    } catch {
      return '';
    }
  }, [params]);

  useEffect(() => {
    let active = true;
    setBusy(true);
    partnerWidgetRequest<{ items: PartnerWidgetCatalogItem[] }>(publicKey, 'catalog', {
      locale,
      partnerOrigin,
    })
      .then((result) => {
        if (!active) return;
        setCatalog(result.items ?? []);
        if (!itemId && result.items?.[0]) setItemId(result.items[0].itemId);
      })
      .catch((error) => active && setMessage(error instanceof Error ? error.message : String(error)))
      .finally(() => active && setBusy(false));
    return () => { active = false; };
  }, [publicKey, locale, partnerOrigin]);

  async function loadAvailability() {
    if (!itemId || !date) return;
    setBusy(true);
    setMessage('');
    try {
      const result = await partnerWidgetRequest<{ slots: PartnerWidgetAvailability[] }>(
        publicKey,
        'availability',
        { itemId, date, partySize, locale, partnerOrigin },
      );
      setSlots(result.slots ?? []);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
      setSlots([]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen bg-white p-4 text-slate-900 sm:p-6">
      <section className="mx-auto w-full max-w-xl rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
        <h1 className="text-2xl font-black">{text.title}</h1>

        <div className="mt-6 grid gap-4">
          <label className="grid gap-2 text-sm font-semibold">
            {text.service}
            <select
              value={itemId}
              onChange={(event) => setItemId(event.target.value)}
              className="min-h-11 rounded-xl border border-slate-300 bg-white px-3"
            >
              {catalog.map((item) => (
                <option key={item.itemId} value={item.itemId}>{item.name}</option>
              ))}
            </select>
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-2 text-sm font-semibold">
              {text.date}
              <input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                className="min-h-11 rounded-xl border border-slate-300 px-3"
              />
            </label>

            <label className="grid gap-2 text-sm font-semibold">
              {text.people}
              <input
                type="number"
                min={1}
                max={50}
                value={partySize}
                onChange={(event) => setPartySize(Math.max(1, Math.min(50, Number(event.target.value) || 1)))}
                className="min-h-11 rounded-xl border border-slate-300 px-3"
              />
            </label>
          </div>

          <button
            type="button"
            onClick={loadAvailability}
            disabled={busy || !itemId || !date}
            className="min-h-12 rounded-xl px-4 font-bold text-white disabled:opacity-50"
            style={{ backgroundColor: accent }}
          >
            {busy ? text.loading : text.search}
          </button>
        </div>

        {message && <p className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">{message}</p>}

        <div className="mt-5 grid gap-3">
          {!busy && date && slots.length === 0 && !message && (
            <p className="text-sm text-slate-500">{text.unavailable}</p>
          )}
          {slots.map((slot) => (
            <button
              key={`${slot.startAt}-${slot.endAt}`}
              type="button"
              className="flex min-h-12 items-center justify-between rounded-xl border border-slate-200 px-4 text-left hover:bg-slate-50"
              onClick={() => {
                const selected = catalog.find((item) => item.itemId === itemId);
                if (!selected?.businessId) {
                  setMessage('Partner booking context unavailable');
                  return;
                }
                const url = new URL(`/booking/${selected.businessId}`, window.location.origin);
                url.searchParams.set('reserveStart', slot.startAt);
                url.searchParams.set('reserveEnd', slot.endAt);
                url.searchParams.set('catalogItemId', itemId);
                url.searchParams.set('partySize', String(partySize));
                window.open(url.toString(), '_top');
              }}
            >
              <span>{new Date(slot.startAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</span>
              <strong style={{ color: accent }}>{text.choose}</strong>
            </button>
          ))}
        </div>

        <p className="mt-7 text-center text-xs text-slate-500">{text.powered}</p>
      </section>
    </main>
  );
}
